import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Button, Form, Input, DatePicker, Tooltip, Select, Checkbox } from "antd";
import type { FormInstance } from "antd";
import ModalWindow from "../../ModalWindow/ModalWindow";
import InputNumberFormat from "../../common/InputNumberFormat";
import { AnyObject } from "../../../lib/interfaces";
import { useAppDispatch, useAppSelector } from "../../..";
import { displayFormat, toHttpFormat, toHttpFormatStartOfDay } from "../../../lib/utils";
import { AgreementsSearchModal } from "../../AgreementsSearch";
import {
  MENU_ACCOUNTS,
  MENU_ACCOUNTS_INDIVIDUAL,
  MENU_ACCOUNTS_INDIVIDUAL_WRITE,
  MENU_ACCOUNTS_ORGANIZATION,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
  MENU_ACCOUNTS_WRITE,
} from "../../../lib/const/permissions";
import { agreementsSearchActions } from "../../../store/slices/agreementsSearch/agreementsSearch";
import { Agreement, IClientType } from "../../../lib/types";
import { ReactComponent as LoupeIcon } from "../../../img/loupe-icon.svg";
import { limitText } from "../../../lib/utils/limitText";
import { hasAccess } from "../../RolesModule/hasAccess";
import "./PaymentRegisterModal.less";
import { postRegisterPayment } from "../../../store/slices/payments";

interface AgreementForRebase {
  agreementId: number;
  agreementNumber: string;
  accountId: number;
  fullName: string;
  inn: string | number | undefined;
  fromDate: string | null;
}

interface ComponentProps {
  visibleRegisterPaymentModal: boolean;
  setVisibleRegisterPaymentModal: React.Dispatch<React.SetStateAction<boolean>>;
  agreementForRebase: AgreementForRebase | undefined;
  setAgreementForRebase: React.Dispatch<React.SetStateAction<AgreementForRebase | undefined>>;
  isAccount: boolean;
  updatePayments: () => void;
}

export const PaymentRegisterModal = (props: ComponentProps) => {
  const {
    setVisibleRegisterPaymentModal,
    visibleRegisterPaymentModal,
    agreementForRebase,
    setAgreementForRebase,
    isAccount,
    updatePayments,
  } = props;

  const dispatch = useAppDispatch();

  const { dicCurrencies, paymentSourceTypes } = useAppSelector((state) => state.rtkDictionaries);
  const { tenant } = useAppSelector((state) => state.tenants);
  const { agreements } = useAppSelector((state) => state.agreements);
  const currencyId = tenant.defaultCurrencyId;

  const [isClicked, setIsClicked] = useState(false);

  const [registerPaymentForm] = Form.useForm();

  const [searchClientType, setSearchClientType] = useState<IClientType>("ORGANIZATION");

  const hasAccessToOrganization = useMemo(
    () =>
      hasAccess(
        MENU_ACCOUNTS_ORGANIZATION,
        MENU_ACCOUNTS_ORGANIZATION_WRITE,
        MENU_ACCOUNTS,
        MENU_ACCOUNTS_WRITE
      ),
    [hasAccess]
  );

  const hasAccessToIndividual = useMemo(
    () => hasAccess(MENU_ACCOUNTS_INDIVIDUAL, MENU_ACCOUNTS_INDIVIDUAL_WRITE),
    [hasAccess]
  );

  const agreementTitle = useMemo(() => {
    if (!agreementForRebase) return "";
    return `${agreementForRebase.agreementNumber} / ${agreementForRebase.fullName}`;
  }, [agreementForRebase]);

  // Кнопка изменения задизейблена, если доступен только один вид клиента
  const allowedClientChange = useMemo(() => {
    if (hasAccessToOrganization && hasAccessToIndividual) {
      return true;
    }
    return false;
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  // Дефолтный тип клиента в поиске ЮЛ/ФЛ
  useEffect(() => {
    if (hasAccessToOrganization) {
      setSearchClientType("ORGANIZATION");
    } else if (hasAccessToIndividual) {
      setSearchClientType("INDIVIDUAL");
    }
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  const toggleSearchClientType = () => {
    if (allowedClientChange) {
      setSearchClientType((prev) => (prev === "INDIVIDUAL" ? "ORGANIZATION" : "INDIVIDUAL"));
      dispatch(agreementsSearchActions.cleanAccounts());
    }
  };

  const onChangeAgreement = (agreement: AgreementForRebase) => {
    setAgreementForRebase(agreement);

    const selectedFromDate = registerPaymentForm.getFieldValue("sourceDate");

    if (selectedFromDate && moment(selectedFromDate).isBefore(agreement.fromDate)) {
      registerPaymentForm.setFieldsValue({
        sourceDate: undefined,
      });
    }
  };

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const onCloseSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  const onSubmitSearchModal = (
    v,
    agreement?: {
      agreementId: number;
      agreementNumber: string;
      accountId: number;
      fullName: string;
      inn: string | number | undefined;
      fromDate: string;
    }[]
  ) => {
    onCloseSearchModal();

    if (agreement) {
      onChangeAgreement(agreement[0]);
      registerPaymentForm.setFieldsValue({
        agreementId: agreement[0].agreementId,
      });
      setIsSearchModalOpen(false);
    }
  };

  // Очистка списка accountSelect при входе и выходе
  useEffect(() => {
    dispatch(agreementsSearchActions.cleanAccounts());
    return () => {
      dispatch(agreementsSearchActions.cleanAccounts());
    };
  }, []);

  const closePaymentCreateModal = () => {
    registerPaymentForm.resetFields();
    setAgreementForRebase(undefined);
    setVisibleRegisterPaymentModal(false);
    setIsClicked(false);
  };

  const submitPaymentCreateModal = () => {
    if (registerPaymentForm && !isClicked) {
      registerPaymentForm.submit();
    }
  };

  const onFinish = (values) => {
    setIsClicked(true);
    const params = {
      payments: [
        {
          agreementId: agreementForRebase?.agreementId,
          sourceTypeId: values.sourceTypeId,
          sourceDate: toHttpFormatStartOfDay(values.sourceDate),
          currencyId: values.currencyId,
          currencyRateTypeId: 1,
          currencyAmount: values.amount,
          amount: values.amount,
          paymentDate: toHttpFormatStartOfDay(values.sourceDate),
          parentPaymentId: null,
          info: {
            payerId: agreementForRebase?.inn || undefined,
            purpose: values.purpose,
            documentNumber: values.documentNumber,
          },
          dsc: values.dsc,
        },
      ],
      reason: values.reason,
      useNonTargeted: values.useNonTargeted,
    };

    // @ts-ignore
    dispatch(postRegisterPayment(params)).then((res) => {
      if (res.payload) {
        updatePayments();
        registerPaymentForm.resetFields();
        setIsClicked(false);
        closePaymentCreateModal();
      }
    });
  };

  return (
    <>
      <ModalWindow
        visible={visibleRegisterPaymentModal}
        title="Зарегистрировать платеж"
        handleSubmit={submitPaymentCreateModal}
        handleCancel={closePaymentCreateModal}
        handleReset={closePaymentCreateModal}
        cancelTextButton="Отмена"
        submitTextButton="Сохранить"
        width={530}
        disabled={false}
        submitTooltip=""
        className="payment-register-modal"
      >
        <Form
          form={registerPaymentForm}
          onFinish={onFinish}
          labelCol={{ span: 10 }}
          initialValues={{
            sourceDate: moment(),
            currencyId,
            // sourceTypeId: 2,
            useNonTargeted: false,
          }}
        >
          <Form.Item
            className={`payment-register-modal__agreement form-item-required__hidden-error__mb0 ${
              isAccount ? "isAccount" : "isNotAccount"
            }`}
            label={
              <div>
                Номер договора
                {!isAccount && (
                  <Tooltip title="Открыть расширенный поиск">
                    <LoupeIcon
                      onClick={() => {
                        setIsSearchModalOpen(true);
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            }
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
            labelCol={{ span: 8 }}
            labelAlign="left"
            name="agreementId"
          >
            {isAccount ? (
              <Select
                onChange={(value) => {
                  if (value) {
                    const findAgreement = (agreements as Agreement[])?.find(
                      (item) => item.id === value
                    );

                    if (findAgreement) {
                      const inn =
                        searchClientType === "ORGANIZATION"
                          ? findAgreement?.account?.organization?.properties.find(
                              ({ propertyTypeAlias }) => propertyTypeAlias === "INN"
                            )?.stringValue
                          : undefined;

                      onChangeAgreement({
                        agreementId: findAgreement.id,
                        agreementNumber: findAgreement.agreementNumber,
                        fullName: "",
                        accountId: findAgreement.accountId,
                        inn,
                        fromDate: findAgreement.fromDate,
                      });
                    }
                  }
                }}
                placeholder="Выберите договор"
              >
                {agreements &&
                  agreements?.length !== 0 &&
                  agreements
                    .slice()
                    .sort(({ id: aId }, { id: bId }) => (aId > bId ? 1 : -1))
                    .sort(({ agreementStatusId: aStatus }, { agreementStatusId: bStatus }) => {
                      const statusOrder = (status: number) => (status === 6 ? 1 : 0);
                      return statusOrder(aStatus) - statusOrder(bStatus);
                    })
                    .map((item: { id: number; agreementNumber: string }) => (
                      <Select.Option key={item.id} value={item.id}>
                        {item.agreementNumber}
                      </Select.Option>
                    ))}
              </Select>
            ) : (
              <Tooltip title={agreementTitle} placement="topLeft">
                <div>
                  <Input
                    placeholder="Договор не выбран"
                    readOnly
                    tabIndex={-1}
                    onFocus={(e) => e.target.blur()}
                    value={limitText(agreementTitle, 50)}
                  />
                </div>
              </Tooltip>
            )}
          </Form.Item>

          <Form.Item
            name="sourceDate"
            label="Дата платежа"
            labelCol={{ span: 8 }}
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
          >
            <DatePicker
              style={{ width: "100%" }}
              format={displayFormat}
              disabledDate={(date) => {
                const fromDate = agreementForRebase?.fromDate;

                return (
                  date.isAfter(moment().endOf("day"), "day") ||
                  (fromDate ? date.isBefore(fromDate, "day") : false)
                );
              }}
            />
          </Form.Item>

          <Form.Item
            label="Источник платежа"
            name="sourceTypeId"
            labelCol={{ span: 8 }}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
          >
            <Select placeholder="Выберите источник платежа" allowClear>
              {paymentSourceTypes
                .filter(({ id }) => id !== 128)
                .map(({ id, name }) => (
                  <Select.Option value={id} key={id} className="option">
                    {name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="documentNumber"
            label={<div style={{ whiteSpace: "pre-wrap" }}>Номер платежного документа</div>}
            labelCol={{ span: 8 }}
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
          >
            <Input style={{ width: "100%" }} placeholder="Введите номер платежного документа" />
          </Form.Item>

          <Form.Item
            name="purpose"
            label="Назначение платежа"
            labelCol={{ span: 8 }}
            rules={[
              {
                required: true,
                message: "",
              },
            ]}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
          >
            <Input style={{ width: "100%" }} placeholder="Введите назначение платежа" />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Сумма"
            labelCol={{ span: 8 }}
            rules={[
              {
                required: true,
                message: "",
              },
              {
                validator: (_, value) => {
                  if (value && parseFloat(value) === 0) {
                    return Promise.reject(new Error("Сумма не может быть равна 0"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
          >
            <InputNumberFormat
              thousandSeparator={" "}
              decimalScale={2}
              allowNegative={false}
              placeholder="Введите сумму платежа"
            />
          </Form.Item>

          <Form.Item
            label="Валюта"
            name="currencyId"
            labelCol={{ span: 8 }}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
          >
            <Select className="upload-statement__select" placeholder="Выберите валюту" disabled>
              {dicCurrencies.map(({ id, name }) => (
                <Select.Option value={id} key={id} className="option">
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="reason"
            label="Обоснование"
            labelCol={{ span: 8 }}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
          >
            <Input style={{ width: "100%" }} placeholder="Введите обоснование" />
          </Form.Item>

          <Form.Item
            name="dsc"
            label="Комментарий"
            labelCol={{ span: 8 }}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
          >
            <Input style={{ width: "100%" }} placeholder="Введите комментарий" />
          </Form.Item>

          <Form.Item
            name="useNonTargeted"
            label={
              <span
                title="Использовать для автоматического покрытия"
                style={{
                  whiteSpace: "pre-wrap",
                  marginBottom: 3,
                }}
              >
                Использовать для автомат. покрытия
              </span>
            }
            valuePropName="checked"
            labelCol={{ span: 8 }}
            labelAlign="left"
            className="form-item-required__hidden-error__mb0"
          >
            <Checkbox style={{ marginTop: 11 }} />
          </Form.Item>
        </Form>
      </ModalWindow>
      <AgreementsSearchModal
        clientType={searchClientType}
        toggleClientType={toggleSearchClientType}
        visible={isSearchModalOpen}
        onClose={onCloseSearchModal}
        onSubmit={onSubmitSearchModal}
        okText="Выбрать договор"
      />
    </>
  );
};
